import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

interface ArgoCluster {
  name: string;
  server: string;
}

/**
 * ArgoCDClusterPicker
 * 
 * 自定义字段：从 ArgoCD 动态加载可用的 Kubernetes 集群列表
 * 
 * 使用方式：
 * ```yaml
 * cluster:
 *   title: K8s 集群
 *   type: string
 *   ui:field: ArgoCDClusterPicker
 * ```
 */
export const ArgoCDClusterPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  idSchema,
  schema,
}: FieldProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [clusters, setClusters] = useState<ArgoCluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const loadClusters = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const backendUrl = await discoveryApi.getBaseUrl('argocd');
        const { token } = await identityApi.getCredentials();

        // 从 ArgoCD 后端获取集群列表
        const response = await fetch(
          `${backendUrl}/clusters`,
          {
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load clusters: ${response.statusText}`);
        }

        const data = await response.json();
        const clusterList = (data.items || []).map((cluster: any) => ({
          name: cluster.name,
          server: cluster.server,
        }));

        setClusters(clusterList);
      } catch (err) {
        console.error('Failed to load ArgoCD clusters:', err);
        setError(err instanceof Error ? err.message : 'Failed to load clusters');
      } finally {
        setLoading(false);
      }
    };

    loadClusters();
  }, [discoveryApi, identityApi]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || 'K8s Cluster'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && clusters.length === 0 && (
          <MenuItem value="" disabled>
            无可用集群
          </MenuItem>
        )}
        {clusters.map(cluster => (
          <MenuItem key={cluster.name} value={cluster.name}>
            {cluster.name}
          </MenuItem>
        ))}
      </Select>
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
    </FormControl>
  );
};

